/**
 * Minimal rich-text editor for body copy in the website builder panel.
 * Supports bold, italic, underline, and hyperlinks via execCommand.
 * Stores/accepts HTML strings — plain text is backward-compatible.
 */
import { useRef, useEffect, useState } from "react"
import { Bold, Italic, Underline, Link2 } from "lucide-react"
import { sanitizeHtml } from "@/lib/sanitizeHtml"

const RICH_TEXT_CSS = `
  .rt-editor:empty::before {
    content: attr(data-placeholder);
    color: rgba(255,255,255,0.2);
    pointer-events: none;
  }
  .rt-editor a { color: #60a5fa; text-decoration: underline; cursor: pointer; }
`

interface RichTextInputProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
}

export function RichTextInput({ value, onChange, placeholder }: RichTextInputProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const isFocusedRef = useRef(false)
  const savedRangeRef = useRef<Range | null>(null)
  const [linkMode, setLinkMode] = useState(false)
  const [linkUrl, setLinkUrl] = useState("")
  const linkInputRef = useRef<HTMLInputElement>(null)

  // Set initial HTML on mount. Stored values may carry imported third-party
  // markup — sanitize before it can execute in the admin's session.
  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = sanitizeHtml(value)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync external value changes while not focused
  useEffect(() => {
    if (editorRef.current && !isFocusedRef.current) {
      const clean = sanitizeHtml(value)
      if (editorRef.current.innerHTML !== clean) {
        editorRef.current.innerHTML = clean
      }
    }
  }, [value])

  const emit = () => {
    if (!editorRef.current) return
    // Contenteditable inserts non-breaking spaces (&nbsp; entities /  )
    // for consecutive or trailing spaces. Stored content must carry the plain
    // spaces the user typed — several sections render body as plain text,
    // where the raw entity would display literally.
    // Sanitize the emitted HTML so live payloads can never be (re-)persisted
    // from the editor.
    onChange(sanitizeHtml(editorRef.current.innerHTML).replace(/&nbsp;|\u00a0/g, " "))
  }

  const applyFormat = (e: React.MouseEvent, cmd: string) => {
    e.preventDefault() // preserve selection
    document.execCommand(cmd, false, undefined)
    emit()
  }

  const handleLinkMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange()
    }
    setLinkMode(true)
    setLinkUrl("")
    setTimeout(() => linkInputRef.current?.focus(), 0)
  }

  const commitLink = () => {
    const url = linkUrl.trim()
    if (url && editorRef.current) {
      editorRef.current.focus()
      const sel = window.getSelection()
      if (sel && savedRangeRef.current) {
        sel.removeAllRanges()
        sel.addRange(savedRangeRef.current)
      }
      const href = url.startsWith("http") ? url : `https://${url}`
      document.execCommand("createLink", false, href)
      emit()
    }
    setLinkMode(false)
    setLinkUrl("")
    savedRangeRef.current = null
  }

  const cancelLink = () => {
    setLinkMode(false)
    setLinkUrl("")
    savedRangeRef.current = null
  }

  const btnStyle: React.CSSProperties = {
    width: 22,
    height: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.04)",
    color: "rgba(255,255,255,0.55)",
    cursor: "pointer",
    padding: 0,
    flexShrink: 0,
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <style>{RICH_TEXT_CSS}</style>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 3 }}>
        <button type="button" style={btnStyle} title="Bold" onMouseDown={(e) => applyFormat(e, "bold")}>
          <Bold size={11} strokeWidth={2.5} />
        </button>
        <button type="button" style={btnStyle} title="Italic" onMouseDown={(e) => applyFormat(e, "italic")}>
          <Italic size={11} strokeWidth={2.5} />
        </button>
        <button type="button" style={btnStyle} title="Underline" onMouseDown={(e) => applyFormat(e, "underline")}>
          <Underline size={11} strokeWidth={2.5} />
        </button>
        <button type="button" style={btnStyle} title="Link" onMouseDown={handleLinkMouseDown}>
          <Link2 size={11} strokeWidth={2.5} />
        </button>
      </div>
      {/* Inline link URL input */}
      {linkMode && (
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <input
            ref={linkInputRef}
            type="url"
            placeholder="https://..."
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commitLink() }
              if (e.key === "Escape") cancelLink()
            }}
            style={{
              flex: 1,
              padding: "3px 7px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 6,
              color: "#fff",
              fontSize: 11,
              outline: "none",
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
          <button
            type="button"
            onClick={commitLink}
            style={{ ...btnStyle, color: "#4ade80", border: "1px solid rgba(74,222,128,0.25)" }}
          >
            ✓
          </button>
          <button
            type="button"
            onClick={cancelLink}
            style={{ ...btnStyle, color: "#f87171", border: "1px solid rgba(248,113,113,0.25)" }}
          >
            ✕
          </button>
        </div>
      )}
      {/* Editable body */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="rt-editor"
        data-placeholder={placeholder ?? "Enter text…"}
        onFocus={() => { isFocusedRef.current = true }}
        onBlur={() => { isFocusedRef.current = false; emit() }}
        onInput={emit}
        style={{
          minHeight: 56,
          padding: "6px 8px",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 8,
          color: "#fff",
          fontSize: 12,
          lineHeight: 1.55,
          fontFamily: "'DM Sans', sans-serif",
          outline: "none",
          wordBreak: "break-word",
        }}
      />
    </div>
  )
}
